/**
 * Capacitor initialization & native bridge helpers.
 *
 * This module is loaded once at app startup (see `src/index.js`) and:
 *   - Detects if we are running inside the native shell (Android / iOS)
 *   - Configures status bar + hides splash once React has mounted
 *   - Registers Push Notifications and forwards FCM/APNS tokens to backend
 *   - Wires up hardware back button, network status, and deep links
 *
 * If the app is running in a regular browser, every method is a no-op so the
 * web build keeps behaving exactly as before.
 */
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { PushNotifications } from '@capacitor/push-notifications';

// Global callback set by React <App/> so we can navigate + toast from the
// deep-link listener below without importing router/toast statically (which
// would create a circular import). Set once in App.js via
// `registerPaymentReturnHandler`.
let _paymentReturnHandler = null;
export const registerPaymentReturnHandler = (fn) => {
  _paymentReturnHandler = typeof fn === 'function' ? fn : null;
};

export const isNativeApp = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

/**
 * Public-facing web URL of Bookvia. Used to redirect from the native app to
 * the website when we DON'T want to process a payment / subscription inside
 * the app (avoids Apple's 30% + Google's 15-30% in-app commission).
 *
 * Reads from REACT_APP_PUBLIC_WEB_URL with a sensible production fallback.
 *
 * We use the `www.` subdomain explicitly because Vercel is currently
 * provisioning the SSL certificate for the apex domain (bookvia.app) and the
 * apex returns ERR_CERT_AUTHORITY_INVALID, breaking external payment flows on
 * the native app. Once Vercel finishes apex SSL provisioning we can switch
 * back to the bare apex if desired.
 */
export const PUBLIC_WEB_URL = (
  process.env.REACT_APP_PUBLIC_WEB_URL || 'https://www.bookvia.app'
).replace(/\/$/, '');

/**
 * Open a URL.
 *   - In the native app -> launches an in-app browser (Capacitor Browser
 *     plugin). The user can close it and come back to Bookvia.
 *   - On the web -> regular window.open / navigation.
 *
 * Use this for any flow that involves payment, subscription, or any action
 * that Apple/Google might consider "digital purchase" — sending users to the
 * website avoids the store commission.
 */
export const openExternalUrl = async (url, { newTab = true } = {}) => {
  if (!url) return;
  if (isNativeApp()) {
    try {
      await Browser.open({
        url,
        presentationStyle: 'popover',
        toolbarColor: '#F05D5E',
      });
      return;
    } catch (e) {
      console.warn('[Capacitor] Browser.open failed, falling back', e);
    }
  }
  if (newTab) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    window.location.href = url;
  }
};

/**
 * Convenience helper for business onboarding / paid flows.
 * On native -> opens https://bookvia.app/<path> in the in-app browser.
 * On web    -> calls navigate('/<path>') normally (no redirect needed).
 *
 * Example:
 *   handleBusinessRegister(navigate);
 *   handlePaidFlow(navigate, '/business/upgrade');
 */
export const openExternalBookviaFlow = async (navigate, path) => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  if (isNativeApp()) {
    await openExternalUrl(`${PUBLIC_WEB_URL}${cleanPath}`);
  } else if (typeof navigate === 'function') {
    navigate(cleanPath);
  } else {
    window.location.href = cleanPath;
  }
};

/**
 * Called once during app boot. Safe to call on web - it short-circuits.
 */
export const initCapacitor = async () => {
  if (!isNativeApp()) return;

  // Tag body so CSS can apply safe-area padding only on native
  try {
    document.body.classList.add('capacitor-native');
    document.body.classList.add(`capacitor-${getPlatform()}`);
  } catch (e) { /* ignore */ }

  try {
    // 1) Status bar — On iOS/Android, Style.Light means "light background
    //    with DARK text" (confusingly). Since Bookvia's header is cream,
    //    we want dark time/battery so they remain readable.
    await StatusBar.setStyle({ style: Style.Light });
    if (getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#FBF3EA' });
    }
  } catch (e) {
    console.warn('[Capacitor] StatusBar setup failed', e);
  }

  // 2) Hide splash after a short delay so React has mounted
  setTimeout(async () => {
    try {
      await SplashScreen.hide();
    } catch (e) {
      // ignore
    }
  }, 600);

  // 3) Hardware back button (Android) — when user is at root, exit app
  try {
    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });
  } catch (e) {
    // ignore
  }

  // 3.5) Deep-link listener — the Stripe success/cancel web pages fire a
  // `bookvia://payment/success` or `bookvia://payment/cancel` URL after
  // rendering. Android/iOS then delivers that URL here. We close the
  // In-App Browser sheet (Capacitor Browser) and let the app handle the
  // navigation + toast via the payment-return handler registered by <App/>.
  try {
    App.addListener('appUrlOpen', async (event) => {
      const raw = event?.url || '';
      if (!raw.startsWith('bookvia://')) return;
      try {
        await Browser.close();
      } catch (_) { /* browser may already be closed */ }

      // Parse `bookvia://payment/success?session_id=...&booking_id=...`
      let path = '';
      let query = {};
      try {
        const u = new URL(raw);
        // For custom-scheme URLs, `pathname` in the URL API can be empty on
        // some engines — fall back to a manual split.
        path = (u.host + u.pathname).replace(/^\/+/, '');
        u.searchParams.forEach((v, k) => { query[k] = v; });
      } catch {
        const parts = raw.replace('bookvia://', '').split('?');
        path = parts[0] || '';
        const qs = parts[1] || '';
        qs.split('&').filter(Boolean).forEach((pair) => {
          const [k, v] = pair.split('=');
          if (k) query[decodeURIComponent(k)] = decodeURIComponent(v || '');
        });
      }

      if (_paymentReturnHandler) {
        try { _paymentReturnHandler({ path, query, raw }); } catch (_) { /* ignore */ }
      }
    });
  } catch (e) {
    console.warn('[Capacitor] appUrlOpen listener failed', e);
  }

  // 4) Request push-notifications permission early on Android 13+ so the
  // system dialog appears the very first time the app opens (before login).
  // If the user is already logged in when this runs, we ALSO register the
  // FCM token in the same call.
  try {
    await requestPushPermissionEarly();
  } catch (e) {
    // ignore
  }
};

/**
 * Fire the permission prompt as soon as the app starts (Android 13+ requires
 * this). If already granted or denied, this is a no-op. If a token exists
 * for the logged-in user, we also register it with the backend.
 */
const requestPushPermissionEarly = async () => {
  if (!isNativeApp()) return;
  try {
    let permission = await PushNotifications.checkPermissions();
    if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
      permission = await PushNotifications.requestPermissions();
    }
    if (permission.receive === 'granted') {
      // If the user is already logged in, register the token right away.
      const authToken = typeof localStorage !== 'undefined'
        ? localStorage.getItem('bookvia-token')
        : null;
      if (authToken) {
        // Fire and forget - errors are logged internally
        enablePushForCurrentUser().catch(() => {});
      }
    }
  } catch (e) {
    console.warn('[Capacitor] Early push permission request failed', e);
  }
};

/**
 * Register the current device for push notifications and POST the FCM token
 * to the backend so it can target this user. Safe to call multiple times
 * (the backend upserts by token).
 *
 * Called after every successful login. No-op on web.
 */
export const registerPushNotifications = async (postTokenFn) => {
  if (!isNativeApp()) return null;

  try {
    let permission = await PushNotifications.checkPermissions();
    if (permission.receive === 'prompt') {
      permission = await PushNotifications.requestPermissions();
    }
    if (permission.receive !== 'granted') {
      console.warn('[Capacitor] Push permission denied');
      return null;
    }

    return new Promise((resolve) => {
      let resolved = false;

      PushNotifications.addListener('registration', async (token) => {
        if (resolved) return;
        resolved = true;
        try {
          if (postTokenFn) {
            await postTokenFn({
              token: token.value,
              platform: getPlatform(),
            });
          }
        } catch (e) {
          console.warn('[Capacitor] Failed to send token to backend', e);
        }
        resolve(token.value);
      });

      PushNotifications.addListener('registrationError', (err) => {
        if (resolved) return;
        resolved = true;
        console.error('[Capacitor] Push registration error', err);
        resolve(null);
      });

      PushNotifications.register();
    });
  } catch (e) {
    console.error('[Capacitor] registerPushNotifications failed', e);
    return null;
  }
};

/**
 * Higher-level wrapper that wires the FCM token directly to our backend's
 * /api/push/register endpoint using the user's stored auth token. Call this
 * after every successful login.
 */
export const enablePushForCurrentUser = async () => {
  if (!isNativeApp()) return null;
  const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const authToken = typeof localStorage !== 'undefined'
    ? localStorage.getItem('bookvia-token')
    : null;
  if (!backend || !authToken) return null;

  return registerPushNotifications(async ({ token, platform }) => {
    await fetch(`${backend}/api/push/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ token, platform }),
    });
  });
};

/**
 * Add a listener for incoming push notification taps. The callback receives
 * the parsed `data` payload so the app can navigate to the right screen
 * (e.g. open the booking detail when the user taps a reminder).
 */
export const onPushNotificationTap = (callback) => {
  if (!isNativeApp()) return () => {};
  const sub = PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (notif) => {
      try {
        callback(notif.notification?.data || {});
      } catch (e) {
        console.warn('[Capacitor] push tap handler failed', e);
      }
    },
  );
  return () => sub.then((s) => s.remove());
};


/**
 * Update the native StatusBar to visually match the current account mode.
 * Called by the Navbar whenever the active session changes.
 *   - business  → dark icons on cream (matches negocio navbar background)
 *   - personal  → dark icons on cream (Bookvia brand)
 * Safe to call on web (short-circuits).
 */
export const applyStatusBarForMode = async (mode) => {
  if (!isNativeApp()) return;
  try {
    await StatusBar.setStyle({ style: Style.Light });
    if (getPlatform() === 'android') {
      const color = mode === 'business' ? '#FBF3EA' : '#FBF3EA';
      await StatusBar.setBackgroundColor({ color });
    }
  } catch (e) {
    console.warn('[Capacitor] applyStatusBarForMode failed', e);
  }
};
